import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { blogPosts, categories } from "../data/blog-posts";

import ImgTech from "../assets/images/banner-images/banner-enterprise.jpg";
import ImgAI from "../assets/images/blog-ai-banner.svg";
import ImgIndustry from "../assets/images/banner-images/banner-tdx.jpg";
import ImgAPI from "../assets/images/banner-images/banner-development-api.jpg";

const categoryImages = {
  "Travel Technology": ImgTech,
  "AI in Travel": ImgAI,
  "Industry Insights": ImgIndustry,
  "API Integration": ImgAPI,
};

const categoryColors = {
  "Travel Technology": "#0d6efd",
  "AI in Travel": "#e85d04",
  "Industry Insights": "#6f42c1",
  "API Integration": "#198754",
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const BlogCard = ({ post }) => (
  <div className="col-lg-4 col-md-6 mb-4">
    <div className="blog-card h-100 d-flex flex-column shadow-sm" style={{ borderRadius: 10, overflow: "hidden", background: "#fff", border: "1px solid #eee" }}>
      <div style={{ position: "relative", overflow: "hidden", height: 200 }}>
        <img
          src={categoryImages[post.category] || ImgTech}
          alt={post.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }}
          onMouseOver={e => (e.currentTarget.style.transform = "scale(1.05)")}
          onMouseOut={e => (e.currentTarget.style.transform = "scale(1)")}
        />
        <span
          style={{
            position: "absolute", top: 12, left: 12,
            background: categoryColors[post.category] || "#0d6efd",
            color: "#fff", fontSize: 11, fontWeight: 600,
            padding: "3px 10px", borderRadius: 20, letterSpacing: 0.5
          }}
        >
          {post.category}
        </span>
      </div>
      <div className="p-4 d-flex flex-column flex-grow-1">
        <div className="d-flex align-items-center mb-2" style={{ gap: 12, fontSize: 12, color: "#888" }}>
          <span><i className="far fa-clock me-1"></i>{post.readTime} read</span>
          <span><i className="far fa-calendar me-1"></i>{formatDate(post.date)}</span>
        </div>
        <h5 className="mb-2" style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4, color: "#1a1a2e" }}>
          <Link to={`/blog/${post.slug}`} style={{ color: "inherit", textDecoration: "none" }}>
            {post.title}
          </Link>
        </h5>
        <p className="text-secondary mb-3" style={{ fontSize: 13, lineHeight: 1.6, flexGrow: 1 }}>
          {post.excerpt}
        </p>
        <div className="d-flex align-items-center justify-content-between mt-auto">
          <span style={{ fontSize: 12, color: "#555", fontWeight: 500 }}>
            <i className="far fa-user me-1"></i>{post.author}
          </span>
          <Link
            to={`/blog/${post.slug}`}
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: 12, padding: "4px 14px", borderRadius: 20 }}
          >
            Read More
          </Link>
        </div>
        <div className="mt-3" style={{ borderTop: "1px solid #f0f0f0", paddingTop: 10 }}>
          {post.tags.slice(0, 3).map(tag => (
            <span key={tag} style={{ display: "inline-block", fontSize: 11, color: "#666", background: "#f5f5f5", borderRadius: 12, padding: "2px 8px", marginRight: 4 }}>
              #{tag.replace(/\s+/g, "")}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(9);

  const filtered = useMemo(() => {
    let posts = activeCategory === "All" ? blogPosts : blogPosts.filter(p => p.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    return [...posts].reverse();
  }, [activeCategory, searchQuery]);

  const visible = filtered.slice(0, visibleCount);

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "TravelCarma Blog",
    "description": "Travel technology insights, AI in travel, and enterprise travel platform engineering from TravelCarma.",
    "url": "https://travelcarma.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "TravelCarma",
      "url": "https://travelcarma.com"
    }
  };

  return (
    <div className="page-inside">
      <Helmet>
        <title>Travel Technology Blog | AI, Booking Engines, API Integration | TravelCarma</title>
        <meta name="description" content="Expert insights on travel technology, AI in travel, supplier API integration, and enterprise travel platform engineering from TravelCarma." />
        <meta name="keywords" content="Travel Technology Blog, AI in Travel, Travel Booking Engine, Supplier API Integration, Travel Platform Engineering, DMC Technology, OTA Technology" />
        <link rel="canonical" href="https://travelcarma.com/blog" />
        <meta property="og:title" content="Travel Technology Blog | TravelCarma" />
        <meta property="og:description" content="Expert insights on travel technology, AI in travel, supplier API integration, and enterprise travel platform engineering." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://travelcarma.com/blog" />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      </Helmet>

      {/* Page Header */}
      <div className="page-title d-flex align-items-center mb-5" style={{ minHeight: 200 }}>
        <div className="container text-center" style={{ position: "relative", zIndex: 2 }}>
          <h1 className="text-white m-0 fw-bold">Travel Technology Insights</h1>
          <h5 className="text-white mt-2 fw-normal">
            Expert perspectives on AI in travel, platform engineering, API integration, and enterprise travel technology.
          </h5>
        </div>
        <img className="page-title-img" src={ImgTech} alt="TravelCarma Blog" />
      </div>

      <div className="container mb-5">
        {/* Search + Filter Row */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-5 mb-3 mb-lg-0">
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setVisibleCount(9); }}
                style={{ paddingLeft: 40, borderRadius: 30, border: "1px solid #ddd", fontSize: 14 }}
              />
              <i className="fas fa-search" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 14 }}></i>
            </div>
          </div>
          <div className="col-lg-7">
            <div className="d-flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setVisibleCount(9); }}
                  className={`btn btn-sm ${activeCategory === cat ? "btn-primary" : "btn-outline-secondary"}`}
                  style={{ borderRadius: 20, fontSize: 13, padding: "5px 16px" }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results count */}
        <p className="text-secondary mb-4" style={{ fontSize: 13 }}>
          {filtered.length === 0
            ? "No articles found."
            : `Showing ${Math.min(visibleCount, filtered.length)} of ${filtered.length} articles${activeCategory !== "All" ? ` in "${activeCategory}"` : ""}`}
        </p>

        {/* Blog Grid */}
        <div className="row">
          {visible.map(post => (
            <BlogCard key={post.id} post={post} />
          ))}
        </div>

        {/* Load More */}
        {visibleCount < filtered.length && (
          <div className="text-center mt-4">
            <button
              className="btn btn-primary px-5"
              style={{ borderRadius: 30 }}
              onClick={() => setVisibleCount(v => v + 9)}
            >
              Load More Articles
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
